import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ModernProductData, ProductContentSection, ContentBlock, ProductAsset } from '@/types/modernProductContent';

export const useModernProductContent = (productId?: string) => {
  return useQuery({
    queryKey: ['modern-product-content', productId],
    queryFn: async (): Promise<ModernProductData | null> => {
      if (!productId) return null;

      // Fetch product basic info with category
      const { data: product, error: productError } = await supabase
        .from('products')
        .select(`
          id, name, description, image_url, price, sku, url_slug,
          categories (
            id, name, parent_id
          )
        `)
        .eq('id', productId)
        .single();

      if (productError) throw productError;
      if (!product) return null;

      // Fetch product content sections
      const { data: sections, error: sectionsError } = await supabase
        .from('product_content_sections')
        .select('*')
        .eq('product_id', productId)
        .eq('is_active', true)
        .order('display_order');

      if (sectionsError) throw sectionsError;

      // Fetch content blocks for all sections
      const sectionIds = sections?.map(s => s.id) || [];
      let blocks: ContentBlock[] = [];
      
      if (sectionIds.length > 0) {
        const { data: blocksData, error: blocksError } = await supabase
          .from('content_blocks')
          .select('*')
          .in('section_id', sectionIds)
          .eq('is_active', true)
          .order('display_order');

        if (blocksError) throw blocksError;
        blocks = (blocksData || []) as ContentBlock[];
      }

      // Fetch product assets
      const { data: assets, error: assetsError } = await supabase
        .from('product_assets')
        .select('*')
        .eq('product_id', productId)
        .eq('is_active', true)
        .order('display_order');

      if (assetsError) throw assetsError;

      return {
        id: product.id,
        name: product.name,
        description: product.description,
        image_url: product.image_url,
        price: product.price,
        sku: product.sku,
        url_slug: product.url_slug,
        categories: product.categories,
        sections: (sections || []) as ProductContentSection[],
        blocks,
        assets: (assets || []) as ProductAsset[]
      };
    },
    enabled: !!productId
  });
};